import { Map } from 'immutable';

import { getProfileSections, setAll } from './sections';
import { getOperationsFromProduct } from '../../product/getOperationsFromProduct';
import { updateTemplateForRetain } from '../../variations/retain';
import { shapeNewProductForAPI } from '../../product/shapeForAPI';
import { filterOperations } from '../../product/operations';
import { convertProduct } from '../../product/convert';
import { getSize } from '../../iterable/getSize';

import { ERROR, NEW, OPERATIONS, VALUE } from '../../../constants/product';
import { CHANNEL_ID, ETSY, SHOPIFY } from '../../../constants/channels';
import { ALL, FORMS } from '../../../constants/profiles';
import { VARIANTS } from '../../../constants/variations';
import { STATUS } from '../../../constants/listings';
import { EMPTY } from '../../../constants';
import { FORM } from '../../../constants/forms';
import ATTRIBUTE from '../../../constants/attributes';

export function filterProfileOperations({ channel, profile }) {
  const template = profile.get('template');
  const sections = profile.get('sections');

  function filterDetailsOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].CATEGORY:
          case OPERATIONS[ETSY].DIGITAL:
          case OPERATIONS[ETSY].IS_SUPPLY:
          case OPERATIONS[ETSY].MATERIALS.CHANGE_TO:
          case OPERATIONS[ETSY].PRODUCTION_PARTNERS:
          case OPERATIONS[ETSY].SECTION:
          case OPERATIONS[ETSY].SHOULD_AUTO_RENEW:
          case OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES:
          case OPERATIONS[ETSY].WHEN_MADE:
          case OPERATIONS[ETSY].WHO_MADE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].CATEGORY:
          case OPERATIONS[SHOPIFY].COLLECTIONS:
          case OPERATIONS[SHOPIFY].PRODUCT_TYPE:
          case OPERATIONS[SHOPIFY].VENDOR: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterFilesOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].FILES.ADD:
          case OPERATIONS[ETSY].FILES.DELETE:
          case OPERATIONS[ETSY].FILES.REPLACE:
          case OPERATIONS[ETSY].FILES.SWAP: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterInventoryOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].QUANTITY:
          case OPERATIONS[ETSY].SKU: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterPersonalizationOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterSearchEngineOptimisatonOperations(operation) {
    switch (channel) {
      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].META_DESCRIPTION:
          case OPERATIONS[SHOPIFY].PAGE_TITLE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterShippingOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].WEIGHT:
          case OPERATIONS[ETSY].LENGTH:
          case OPERATIONS[ETSY].WIDTH:
          case OPERATIONS[ETSY].HEIGHT:
          case OPERATIONS[ETSY].RETURN_POLICY:
          case OPERATIONS[ETSY].SHIPPING_PROFILE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterOperationsBySection({ section, operations }) {
    switch (section) {
      case FORM.PHOTOS: {
        return operations.filter(filterOperations(/^photos/, false));
      }

      case FORM.VIDEOS: {
        return operations.filter(filterOperations(/^videos/, false));
      }

      case FORM.TITLE: {
        return operations.filter(filterOperations(OPERATIONS[channel].TITLE, false));
      }

      case FORM.DESCRIPTION: {
        return operations.filter(filterOperations(OPERATIONS[channel].DESCRIPTION, false));
      }

      case FORM.TAGS: {
        return operations.filter(filterOperations(/^tags/, false));
      }

      case FORM.DETAILS: {
        return operations.filter(filterOperations(filterDetailsOperations));
      }

      case FORM.PRICING: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterOperations(OPERATIONS[ETSY].PRICE, false));
          }

          case SHOPIFY: {
            return sections.get(FORM.VARIATIONS) && getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) > 0
              ? operations
              : operations.filter(filterOperations(OPERATIONS[SHOPIFY].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.SEO: {
        switch (channel) {
          case SHOPIFY: {
            return operations.filter(filterOperations(filterSearchEngineOptimisatonOperations));
          }
          default: {
            return operations;
          }
        }
      }

      case FORM.INVENTORY: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterOperations(filterInventoryOperations));
          }

          case SHOPIFY: {
            return sections.get(FORM.VARIATIONS) && getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) > 0
              ? operations
              : operations.filter(filterOperations(OPERATIONS[SHOPIFY].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.VARIATIONS: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterOperations(OPERATIONS[channel].VARIATIONS, false));
          }

          case SHOPIFY: {
            return (sections.get(FORM.INVENTORY) || sections.get(FORM.PRICING) || sections.get(FORM.SHIPPING))
              ? operations
              : operations.filter(filterOperations(OPERATIONS[channel].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.PERSONALIZATION: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterPersonalizationOperations);
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.SHIPPING: {
        switch (channel) {
          case ETSY: {
            return sections.get(FORM.DETAILS) && template.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])
              ? operations
              : operations.filter(filterShippingOperations);
          }

          case SHOPIFY: {
            return sections.get(FORM.VARIATIONS) && getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) > 0
              ? operations
              : operations.filter(filterOperations(OPERATIONS[SHOPIFY].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.FILES: {
        switch (channel) {
          case ETSY: {
            return sections.get(FORM.DETAILS) && template.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])
              ? operations
              : operations.filter(filterFilesOperations);
          }

          default: {
            return operations;
          }
        }
      }

      default: {
        return operations;
      }
    }
  }

  function reduceSections(operations, selected, section) {
    switch (section) {
      case ALL: {
        return operations;
      }

      default: {
        return selected
          ? operations
          : filterOperationsBySection({ section, operations });
      }
    }
  }

  return sections.get(ALL)
    ? template.get('operations', EMPTY.LIST)
    : sections.reduce(reduceSections, template.get('operations', EMPTY.LIST));
}

export function shapeProfilesForApp({ profiles, response }) {
  const channel = profiles.get('channel');

  function reduceProfiles(result, data) {
    const {
      connected_listings: connectedListings,
      id,
      name,
      product_template_id: templateId,
      sections,
    } = data;

    let profile = Map({
      id,
      listings: Map({ total: connectedListings || 0 }),
      name,
      sections: setAll(getProfileSections({ channel, value: sections })),
      templateId,
    });

    function updateLoading(loading) {
      return loading.add(id);
    }

    function updateProfileIds(profileIds = EMPTY.LIST) {
      return profileIds.push(id);
    }

    function updateTemplateIds(templateIds = EMPTY.LIST) {
      return templateIds.push(templateId);
    }

    function updateTemplateToProfileMap(templateToProfileMap = EMPTY.MAP) {
      return templateToProfileMap.set(templateId, id);
    }

    if (profiles.hasIn(['byId', id, 'template'])) {
      profile = profile.set('template', profiles.getIn(['byId', id, 'template']));
    } else {
      result.profiles = result.profiles.update('loading', updateLoading);
      result.templates = result.templates
        .update('profileIds', updateProfileIds)
        .update('templateIds', updateTemplateIds)
        .update('templateToProfileMap', updateTemplateToProfileMap);
    }

    function updateById(byId) {
      return byId.set(id, profile);
    }

    function updateIds(ids) {
      return ids.push(id);
    }

    result.profiles = result.profiles.update('byId', updateById).update('ids', updateIds);

    if (profiles.getIn(['editing', 'id']) === id) {
      result.profiles = result.profiles.set('editing', profile);
    }

    return result;
  }

  function updatePagination(pagination) {
    const total = response.count;
    const page = pagination.get('page');
    const pageSize = pagination.get('pageSize');
    const offset = page * pageSize;

    return pagination
      .set('end', Math.min(total, offset + pageSize))
      .set('start', offset)
      .set('total', total);
  }

  function updateEditing(editing = EMPTY.MAP) {
    return editing.get('id') === NEW
      ? editing
      : EMPTY.MAP;
  }

  return response.data.reduce(
    reduceProfiles,
    {
      profiles: profiles
        .set('byId', EMPTY.MAP)
        .set('ids', EMPTY.LIST)
        .set('loading', EMPTY.SET)
        .set('total', response.count)
        .set('section', profiles.getIn(['filters', 'selected']))
        .update('editing', updateEditing)
        .update('pagination', updatePagination),
      templates: EMPTY.MAP,
    }
  );
}

function shapeSectionsForAPI({ channel, profile }) {
  function reduceForms(sections, form) {
    const value = profile.getIn(['sections', form]) || false;
    sections[form] = value;
    sections[ALL] = sections[ALL] && value;
    return sections;
  }

  if (profile.getIn(['template', 'channel']) !== channel) {
    return FORMS[channel].reduce(reduceForms, { [ALL]: true });
  }

  return profile.get('sections').toObject();
}

function shapeTemplateForAPI({ channel, db, shopId, template }) {
  let product = template;

  if (product.get('shopId') !== shopId) {
    product = product.set('shopId', shopId);
  }

  if (product.get('channel') !== channel) {
    product = product.set('productId', NEW).set(ERROR, EMPTY.MAP);
    product = convertProduct({ from: product.get('channel'), to: channel, product, validate: true });
    product = updateTemplateForRetain({ channel, template: product });
    product = product.set('operations', getOperationsFromProduct({ channel, product }));
  } else {
    product = updateTemplateForRetain({ channel, template: product });
  }

  return {
    channel_id: CHANNEL_ID[channel],
    operations: shapeNewProductForAPI({ channel, product, shopId, status: STATUS.TEMPLATE }),
    shopId,
    userDb: db,
  };
}

export function shapeProfileForAPI({ channel, db, profile, shopId }) {
  const name = profile.get('name');
  const sections = shapeSectionsForAPI({ channel, profile });
  const template = shapeTemplateForAPI({ channel, db, shopId, template: profile.get('template') });
  return { name, sections, template };
}
